import { proStudioUrqlGql as urqlGql } from '@pypestream/api-services';
import type { WidgetAppConfig } from '@pypestream/pype/customization';

import { checkForErrors } from '../../utils/check-for-errors';

/**
 * Wire-format schema version for the persisted widget design blob. Sent on
 * every mutation so the backend can route forwards-incompatible payloads. We
 * don't currently version the JSON shape on the frontend — bump this only
 * when the structure of `WidgetAppConfig` changes in a way the backend cares
 * about.
 */
const SCHEMA_VERSION = 1;

/**
 * The wire shape, deliberately NOT `WidgetAppConfig`.
 *
 * `branding.customerName` was retired from the design contract — no field, no
 * default, no runtime read — but the mutation input still requires the key, so
 * the wire carries a constant `''`. Keeping the divergence in a type here is
 * what stops "the backend needs it" from turning back into a designer-owned
 * field. Delete this type together with the literal below once the backend
 * stops requiring the key.
 */
type PersistedWidgetAppConfig = Omit<WidgetAppConfig, 'branding'> & {
  branding: WidgetAppConfig['branding'] & { customerName: string };
};

/**
 * Rebuild the payload field-by-field so we persist exactly `WidgetAppConfig`,
 * plus the one backend-required key above.
 *
 * Not a redundant copy: the designer loads the stored blob verbatim and edits
 * it in place, so any key the backend still has — including ones the designer
 * no longer owns — would otherwise ride back out on every save. Projecting
 * drops them at the wire boundary, which is the only place that knows what
 * "persisted" means.
 *
 * Add new `WidgetAppConfig` fields here or they silently stop persisting; the
 * key-set assertions in `mutate-widget-app-design.test.ts` fail loudly if this
 * drifts from the type.
 */
function toPersistedConfig(config: WidgetAppConfig): PersistedWidgetAppConfig {
  const { design, shell, branding } = config;
  return {
    design: {
      theme: design.theme,
      tokens: design.tokens,
      partOverrides: design.partOverrides,
      rawCss: design.rawCss,
    },
    shell: {
      modality: shell.modality,
      size: shell.size,
    },
    branding: {
      // Backend-required, designer-retired — see `PersistedWidgetAppConfig`.
      // Never read from `branding`: a stored blob's stale value must not survive.
      customerName: '',
      displayName: branding.displayName,
      customerLogoUrl: branding.customerLogoUrl,
      bannerUrl: branding.bannerUrl,
      brandMomentUrl: branding.brandMomentUrl,
      launcherName: branding.launcherName,
    },
  };
}

interface MutateArgs {
  appId: string;
  env: 'live' | 'sandbox';
  projectId: string;
  config: WidgetAppConfig;
}

/**
 * Persist a widget design config to the env-specific endpoint. Throws on any
 * GraphQL or network error so callers can `await mutateWidgetAppDesign(...)`
 * inside a `try/catch` and surface a snackbar on failure.
 */
export async function mutateWidgetAppDesign({
  appId,
  env,
  projectId,
  config,
}: MutateArgs): Promise<void> {
  const mutate =
    env === 'live'
      ? urqlGql.mutatePutWidgetAppDesign
      : urqlGql.mutatePutWidgetAppDesignSandbox;

  const res = await mutate({
    appId,
    projectId,
    config: toPersistedConfig(config),
    schemaVersion: SCHEMA_VERSION,
  });

  checkForErrors(res);
}
